/**
 * Réponse de Koffi à « qui est au bureau demain ? » (MOKA-266) : présences
 * regroupées par lieu, le lieu de la personne en premier, noms bornés.
 * Logique pure : `CalendarVoiceHandler` fournit les lignes et le lieu de
 * référence, ces fonctions rédigent la réponse.
 */

export type PresencePortion = 'FullDay' | 'Morning' | 'Afternoon' | 'Evening' | string | null;

export interface PresenceSummaryRow {
  /** `YYYY-MM-DD`. */
  date: string;
  profileId: string;
  /** Nom affiché (libellé de calendrier de la personne, sinon prénom nom). */
  name: string;
  portion: PresencePortion;
  /** Clé stable du lieu : `loc:<id>` (localisation du calendrier) ou `none` (présence sans lieu). */
  placeKey: string;
  placeLabel: string;
}

export interface PlaceGroup {
  key: string;
  label: string;
  /** Noms triés, une personne une fois par lieu (avec sa demi-journée si elle n'est là qu'une partie du jour). */
  names: string[];
}

export interface PresenceSummaryOptions {
  startDate: string;
  endDate: string;
  /** Date du jour `YYYY-MM-DD` (fuseau du navigateur), pour « Aujourd'hui » / « Demain ». */
  today: string;
  /** Lieu de la personne qui demande, affiché en premier (`null` : par fréquentation). */
  homeKey: string | null;
  /** Noms affichés par lieu avant « et N autres » (défaut : 6 sur un jour, 3 sur plusieurs). */
  maxNames?: number;
  /** Jours détaillés avant « et N autres jours » (défaut 7). */
  maxDays?: number;
}

const PORTION_LABELS: Record<string, string> = {
  Morning: 'matin',
  Afternoon: 'après-midi',
  Evening: 'soir'
};

/** Présences d'un jour regroupées par lieu : lieu de référence d'abord, puis le plus fréquenté. */
export function groupDayByPlace(rows: readonly PresenceSummaryRow[], homeKey: string | null): PlaceGroup[] {
  const places = new Map<string, { label: string; people: Map<string, { name: string; portions: Set<string> }> }>();
  for (const r of rows) {
    const place = places.get(r.placeKey) ?? { label: r.placeLabel, people: new Map() };
    const person = place.people.get(r.profileId) ?? { name: r.name, portions: new Set<string>() };
    person.portions.add(r.portion ?? 'FullDay');
    place.people.set(r.profileId, person);
    places.set(r.placeKey, place);
  }

  const groups: PlaceGroup[] = [...places.entries()].map(([key, p]) => ({
    key,
    label: p.label,
    names: [...p.people.values()]
      .sort((a, b) => a.name.localeCompare(b.name, 'fr'))
      .map(person => person.name + portionSuffix(person.portions))
  }));

  return groups.sort((a, b) => {
    if (a.key === homeKey) return -1;
    if (b.key === homeKey) return 1;
    if (a.key === 'none') return 1;
    if (b.key === 'none') return -1;
    return b.names.length - a.names.length || a.label.localeCompare(b.label, 'fr');
  });
}

/** « (matin) » si la personne n'est là qu'une demi-journée ; rien si journée entière ou matin + après-midi. */
function portionSuffix(portions: Set<string>): string {
  if (portions.size !== 1) return '';
  const label = PORTION_LABELS[[...portions][0]];
  return label ? ` (${label})` : '';
}

/** « Alex, Béa, Chloé et 5 autres » */
export function listNames(names: readonly string[], max: number): string {
  if (names.length <= max) return names.join(', ');
  const rest = names.length - max;
  return `${names.slice(0, max).join(', ')} et ${rest} ${rest > 1 ? 'autres' : 'autre'}`;
}

function parseDate(date: string): Date {
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date: string, days: number): string {
  const d = parseDate(date);
  d.setDate(d.getDate() + days);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** « Demain, vendredi 9 octobre » / « Aujourd'hui, jeudi 8 octobre » / « Lundi 12 octobre ». */
export function dayHeading(date: string, today: string): string {
  const label = parseDate(date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  if (date === today) return `Aujourd'hui, ${label}`;
  if (date === addDays(today, 1)) return `Demain, ${label}`;
  if (date === addDays(today, -1)) return `Hier, ${label}`;
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/**
 * Réponse complète. Un jour :
 * « Demain, jeudi 8 octobre — Paris (8) : Alex, … et 2 autres · Lyon (5) : … »
 * Plusieurs jours : une ligne par jour, noms plus serrés.
 */
export function formatPresenceSummary(rows: readonly PresenceSummaryRow[], opts: PresenceSummaryOptions): string {
  const singleDay = opts.startDate === opts.endDate;
  const maxNames = opts.maxNames ?? (singleDay ? 6 : 3);
  const maxDays = opts.maxDays ?? 7;

  if (rows.length === 0) {
    return singleDay
      ? `Personne n'a posé de présence ${lowerFirst(dayHeading(opts.startDate, opts.today))}.`
      : `Aucune présence posée du ${lowerFirst(dayHeading(opts.startDate, opts.today))} au ${lowerFirst(dayHeading(opts.endDate, opts.today))}.`;
  }

  const byDate = new Map<string, PresenceSummaryRow[]>();
  for (const r of rows) {
    byDate.set(r.date, [...(byDate.get(r.date) ?? []), r]);
  }
  const dates = [...byDate.keys()].sort();

  const lines = dates.slice(0, maxDays).map(date => {
    const groups = groupDayByPlace(byDate.get(date)!, opts.homeKey);
    const places = groups
      .map(g => `${g.label} (${g.names.length}) : ${listNames(g.names, maxNames)}`)
      .join(' · ');
    return `${dayHeading(date, opts.today)} — ${places}`;
  });

  const hiddenDays = dates.length - maxDays;
  if (hiddenDays > 0) {
    lines.push(`… et ${hiddenDays} autre${hiddenDays > 1 ? 's' : ''} jour${hiddenDays > 1 ? 's' : ''} : ouvrez le calendrier pour le détail.`);
  }
  return lines.join('\n');
}

/**
 * Lieu de référence de la personne : le premier candidat qui existe parmi les
 * lieux de la réponse. Les candidats sont donnés du plus sûr au moins sûr.
 */
export function pickHomePlace(candidates: readonly (string | null | undefined)[], available: ReadonlySet<string>): string | null {
  for (const c of candidates) {
    if (c && available.has(c)) return c;
  }
  return null;
}

/** Lieu le plus fréquent parmi les présences de la personne elle-même sur la période. */
export function ownMostFrequentPlace(rows: readonly PresenceSummaryRow[], profileId: string): string | null {
  const counts = new Map<string, number>();
  for (const r of rows) {
    if (r.profileId === profileId && r.placeKey !== 'none') {
      counts.set(r.placeKey, (counts.get(r.placeKey) ?? 0) + 1);
    }
  }
  let best: string | null = null;
  let bestCount = 0;
  for (const [key, count] of counts) {
    if (count > bestCount) {
      best = key;
      bestCount = count;
    }
  }
  return best;
}
